import { useEffect, useState } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

interface CountryData {
  name: string;
  iso_code: string;
  trial_count: number;
  percentage: number;
}

interface GeographicData {
  countries: CountryData[];
}

export default function WorldMap() {
  const [geoData, setGeoData] = useState<GeographicData | null>(null);
  const [tooltipContent, setTooltipContent] = useState<string>("");
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);

  useEffect(() => {
    fetch('/data/geographic_distribution.json')
      .then(res => res.json())
      .then(data => setGeoData(data))
      .catch(err => console.error('Failed to load geographic data:', err));
  }, []);

  if (!geoData) {
    return (
      <Card className="border border-[#E4E1D8] shadow-[0_2px_8px_rgba(42,42,40,0.08)] rounded-xl">
        <CardHeader>
          <CardTitle>Global Research Distribution</CardTitle>
          <CardDescription>Loading map data...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Create lookup for trial counts by ISO code
  const countryLookup: Record<string, CountryData> = {};
  geoData.countries.forEach(country => {
    countryLookup[country.iso_code] = country;
  });

  // Calculate color intensity based on trial count
  const maxCount = Math.max(...geoData.countries.map(c => c.trial_count));
  
  const getCountryColor = (isoCode: string) => {
    const country = countryLookup[isoCode];
    if (!country) return "#F4F2EC"; // Paper for countries with no data
    
    const intensity = country.trial_count / maxCount;
    
    // Color scale from paper to gold and ink.
    if (intensity > 0.7) return "#2A2A28"; // Ink
    if (intensity > 0.4) return "#A98A2C"; // Gold
    if (intensity > 0.2) return "#C8A53D"; // Bright gold
    if (intensity > 0.05) return "#B98B36"; // Warm path accent
    return "#F4F2EC"; // Paper
  };

  return (
    <Card className="border border-[#E4E1D8] shadow-[0_2px_8px_rgba(42,42,40,0.08)] rounded-xl">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Global Research Distribution</span>
          <Badge variant="outline" className="bg-[#F4F2EC] text-[#2A2A28] border-[#E4E1D8]">
            {geoData.countries.length} Countries
          </Badge>
        </CardTitle>
        <CardDescription>
          Interactive map showing clinical trial distribution across {geoData.countries.length} countries
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="relative">
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{
              scale: 140,
              center: [0, 30]
            }}
            style={{
              width: "100%",
              height: "auto"
            }}
          >
            <ZoomableGroup>
              <Geographies geography={geoUrl}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const isoCode = geo.id;
                    const country = countryLookup[isoCode];
                    const isHovered = hoveredCountry === isoCode;
                    
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={getCountryColor(isoCode)}
                        stroke="#FFFFFF"
                        strokeWidth={0.5}
                        style={{
                          default: {
                            outline: "none",
                            transition: "all 0.2s"
                          },
                          hover: {
                            fill: country ? "#C26A1E" : "#F4F2EC",
                            outline: "none",
                            strokeWidth: 1,
                            cursor: country ? "pointer" : "default"
                          },
                          pressed: {
                            outline: "none"
                          }
                        }}
                        onMouseEnter={() => {
                          if (country) {
                            setHoveredCountry(isoCode);
                            setTooltipContent(`${country.name}: ${country.trial_count} trials (${country.percentage}%)`);
                          }
                        }}
                        onMouseLeave={() => {
                          setHoveredCountry(null);
                          setTooltipContent("");
                        }}
                      />
                    );
                  })
                }
              </Geographies>
            </ZoomableGroup>
          </ComposableMap>
          
          {/* Tooltip */}
          {tooltipContent && (
            <div className="absolute top-4 left-4 bg-white border-2 border-[#A98A2C] rounded-lg px-4 py-2 shadow-lg z-10">
              <p className="text-sm font-semibold text-[#2A2A28]">{tooltipContent}</p>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="mt-6 flex flex-col gap-4">
          <div>
            <p className="text-sm font-semibold text-[#2A2A28] mb-2">Trial Volume</p>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded" style={{ backgroundColor: "#2A2A28" }}></div>
                <span className="text-xs text-[#6E6B64]">High (70%+)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded" style={{ backgroundColor: "#A98A2C" }}></div>
                <span className="text-xs text-[#6E6B64]">Medium (40-70%)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded" style={{ backgroundColor: "#C8A53D" }}></div>
                <span className="text-xs text-[#6E6B64]">Low (20-40%)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded" style={{ backgroundColor: "#B98B36" }}></div>
                <span className="text-xs text-[#6E6B64]">Minimal (5-20%)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded" style={{ backgroundColor: "#F4F2EC" }}></div>
                <span className="text-xs text-[#6E6B64]">None</span>
              </div>
            </div>
          </div>

          {/* Top Countries List */}
          <div>
            <p className="text-sm font-semibold text-[#2A2A28] mb-2">Top Research Countries</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {geoData.countries.slice(0, 6).map((country) => (
                <div 
                  key={country.iso_code}
                  className="flex items-center justify-between p-2 bg-[#F4F2EC] rounded-lg"
                >
                  <span className="text-sm font-medium text-[#2A2A28]">{country.name}</span>
                  <Badge variant="secondary" className="bg-[#A98A2C] text-white">
                    {country.trial_count}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
